import React, { useState, forwardRef, useImperativeHandle } from 'react';
import Dropzone from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { storage } from '@root/firebase';
import {
  ref as storageRef,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import { db } from '@root/firebase';
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import '@css/custom/dropzone/dropzone.css';

const DocUploader = forwardRef(({ onUpload }, ref) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) {
      toast.error('No file selected');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async (uploadPath, onProgress) => {
    if (!selectedFile) {
      toast.error('No file selected');
      return;
    }

    try {
      const fileSizeInMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
      const metadata = {
        customMetadata: {
          fileSize: `${fileSizeInMB} MB`,
        },
      };

      const fileRef = storageRef(storage, uploadPath);
      const uploadTask = uploadBytesResumable(fileRef, selectedFile, metadata);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            if (onProgress) onProgress(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            toast.error('Failed to upload file');
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({ downloadURL, fileSizeInMB });
          }
        );
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
      throw error;
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  useImperativeHandle(ref, () => ({
    getSelectedFile: () => selectedFile,
    handleUpload,
  }));

  return (
    <div className='doc-uploader'>
      <Dropzone
        onDrop={handleDrop}
        accept={{
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
          'text/plain': ['.txt'],
        }}
        multiple={false}
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <div className='dz-message needsclick'>
              {!selectedFile ? (
                <>
                  <div className='mb-3'>
                    <i className='display-4 text-muted mdi mdi-file-upload'></i>
                  </div>
                  <h5 className='font-size-12'>Drag & Drop a Document Here</h5>
                  <p className='text-muted font-size-12'>
                    Accepted formats: PDF, DOC, DOCX, TXT
                  </p>
                </>
              ) : (
                <div className='file-details'>
                  <p className='mb-1 font-size-12'>
                    <strong>Selected File:</strong> {selectedFile.name}
                  </p>
                  <button
                    className='btn btn-danger btn-sm'
                    onClick={handleRemoveFile}
                  >
                    Remove File
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Dropzone>
    </div>
  );
});

DocUploader.displayName = 'DocUploader';

export default DocUploader;
